// Local edit-draft state for an Artifact + diffing it into a PUT patch.
// Only the editable fields (CONTRACTS §7) are tracked. Tags compare order-sensitively.

import { useMemo, useState } from "react";
import type {
  Artifact,
  EditableNumberField,
  EditableTagField,
  EditableTextField,
  UpdateArtifactRequest,
} from "./types";
import {
  EDITABLE_NUMBER_FIELDS,
  EDITABLE_TAG_FIELDS,
  EDITABLE_TEXT_FIELDS,
} from "./types";

/** All editable fields as strings/arrays for form binding. */
export interface Draft {
  title: string;
  artist: string;
  dateDisplay: string;
  country: string;
  medium: string;
  dimensions: string;
  misc: string;
  captionText: string;
  notes: string;
  yearStart: string; // kept as string for the input; parsed on diff
  yearEnd: string;
  styleTags: string[];
  generalTags: string[];
  tourTags: string[];
}

function toDraft(a: Artifact): Draft {
  return {
    title: a.title ?? "",
    artist: a.artist ?? "",
    dateDisplay: a.dateDisplay ?? "",
    country: a.country ?? "",
    medium: a.medium ?? "",
    dimensions: a.dimensions ?? "",
    misc: a.misc ?? "",
    captionText: a.captionText ?? "",
    notes: a.notes ?? "",
    yearStart: a.yearStart != null ? String(a.yearStart) : "",
    yearEnd: a.yearEnd != null ? String(a.yearEnd) : "",
    styleTags: a.styleTags ? [...a.styleTags] : [],
    generalTags: a.generalTags ? [...a.generalTags] : [],
    tourTags: a.tourTags ? [...a.tourTags] : [],
  };
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Parse a year input; "" -> undefined, otherwise an int (negative = BCE) or NaN-guarded undefined. */
function parseYear(s: string): number | undefined {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

export interface UseArtifactDraft {
  draft: Draft;
  setText: (field: EditableTextField, value: string) => void;
  setNumber: (field: EditableNumberField, value: string) => void;
  setTags: (field: EditableTagField, value: string[]) => void;
  /** Reset the draft to a (possibly updated) source artifact. */
  reset: (a: Artifact) => void;
  /** The minimal PUT patch (only changed fields). */
  diff: () => UpdateArtifactRequest;
  /** Whether anything changed vs the source. */
  dirty: boolean;
}

export function useArtifactDraft(source: Artifact): UseArtifactDraft {
  const [base, setBase] = useState<Artifact>(source);
  const [draft, setDraft] = useState<Draft>(() => toDraft(source));

  const setText = (field: EditableTextField, value: string) =>
    setDraft((d) => ({ ...d, [field]: value }));
  const setNumber = (field: EditableNumberField, value: string) =>
    setDraft((d) => ({ ...d, [field]: value }));
  const setTags = (field: EditableTagField, value: string[]) =>
    setDraft((d) => ({ ...d, [field]: value }));

  const reset = (a: Artifact) => {
    setBase(a);
    setDraft(toDraft(a));
  };

  const diff = (): UpdateArtifactRequest => {
    const patch: UpdateArtifactRequest = {};
    for (const f of EDITABLE_TEXT_FIELDS) {
      const next = draft[f].trim();
      const prev = base[f] ?? "";
      if (next !== prev) patch[f] = next;
    }
    for (const f of EDITABLE_NUMBER_FIELDS) {
      const next = parseYear(draft[f]);
      const prev = base[f];
      if (next !== prev) patch[f] = next;
    }
    for (const f of EDITABLE_TAG_FIELDS) {
      const next = draft[f];
      const prev = base[f] ?? [];
      if (!arraysEqual(next, prev)) patch[f] = next;
    }
    return patch;
  };

  const dirty = useMemo(
    () => Object.keys(diff()).length > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, base],
  );

  return { draft, setText, setNumber, setTags, reset, diff, dirty };
}
